import { useEffect, useState } from 'react';
import { track } from '@/analytics/tracker';
import { Dialog } from '@/app/layers/Dialog';
import { appConfig } from '@/config/app';
import { isCrispMounted, openCrispChat } from '@/integrations/crisp';
import { useT } from '@/i18n/LocaleProvider';
import styles from './HelpDialog.module.css';
import { useShell } from './ShellContext';

/**
 * Помощь (NAV-6): живой чат Crisp, копирование адреса поддержки с fallback на mailto, FAQ лендинга.
 * Кнопка чата есть только при смонтированном виджете: рубильник админки выключен — человек видит
 * адрес поддержки, а не кнопку, которая открыла бы почтовый клиент.
 */
export function HelpDialog() {
  const { helpOpen, closeHelp } = useShell();
  const t = useT();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!helpOpen) return;
    setCopied(false);
    track('help_opened', { metadata: { source: 'menu' } });
  }, [helpOpen]);

  const close = (reason: 'button' | 'escape' | 'overlay' | 'back') => {
    track('help_closed', { metadata: { close_reason: reason } });
    closeHelp(reason);
  };

  const chatAvailable = isCrispMounted();

  const openChat = () => {
    if (openCrispChat()) close('button');
  };

  const copySupport = async () => {
    try {
      await navigator.clipboard.writeText(appConfig.supportEmail);
      setCopied(true);
    } catch {
      window.location.href = `mailto:${appConfig.supportEmail}`;
    }
  };

  return (
    <Dialog open={helpOpen} onClose={() => close('escape')} labelledBy="help-title" maxWidth={420}>
      <h2 id="help-title" className={styles.title}>
        {t('help.title')}
      </h2>
      {chatAvailable && (
        <button type="button" className={styles.action} onClick={openChat}>
          {t('help.chat')}
        </button>
      )}
      <button type="button" className={styles.action} onClick={copySupport}>
        {t(copied ? 'help.copied' : 'help.copy', { email: appConfig.supportEmail })}
      </button>
      <a
        className={styles.action}
        href={appConfig.faqUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('help.faq')}
      </a>
    </Dialog>
  );
}
